/* eslint-disable @nx/enforce-module-boundaries */
import { inject, Injectable } from '@angular/core';
import { Result, ResultStream } from '@causality/core';
import { UnsubscribeFormApi, UnverifiedDonationApi } from '@fmnts/api/next';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import { GeneralHttpError } from '@formunauts/shared/errors/domain';
import { constVoid } from 'effect/Function';
import { isString } from 'effect/Predicate';
import { map } from 'rxjs';
import {
  AuthenticationFormAdapter,
  CheckoutResponseDefaultAdapter,
  CheckoutResponseTamaroAdapter,
  SelfCheckoutFormAdapter,
  UnsubscribeFormAdapter,
} from './self-checkout-adapter';
import { SelfCheckoutRepository } from './self-checkout-repository';
@Injectable()
export class SelfCheckoutApiRepository extends SelfCheckoutRepository {
  private readonly unverifiedDonationApi = inject(UnverifiedDonationApi);
  private readonly unsubscribeFormApi = inject(UnsubscribeFormApi);
  private readonly _authFormAdapter = new AuthenticationFormAdapter();
  private readonly _selfCheckoutAdapter = new SelfCheckoutFormAdapter();
  private readonly _checkoutDefaultResponseAdapter =
    new CheckoutResponseDefaultAdapter();
  private readonly _checkoutTamaroResponseAdapter =
    new CheckoutResponseTamaroAdapter();
  private readonly _unsubscribeFormAdapter = new UnsubscribeFormAdapter();

  override getAuthenticationForm(
    hash: SelfCheckout.Hash,
  ): ResultStream.ResultStream<
    SelfCheckout.AuthenticationForm,
    SelfCheckout.SelfCheckoutError
  > {
    return this.unverifiedDonationApi.get(hash).pipe(
      map((result) => this._authFormAdapter.adapt(result)),
      ResultStream.fromObservable({
        onFailure: (error) => Result.fail(_getAuthError(error)),
      }),
    );
  }

  override authenticate(
    payload: SelfCheckout.AuthenticationData,
  ): ResultStream.ResultStream<
    SelfCheckout.SelfCheckoutForm,
    SelfCheckout.SelfCheckoutError
  > {
    return this.unverifiedDonationApi.authenticateForVerification(payload).pipe(
      map((result) => this._selfCheckoutAdapter.adapt(result)),
      ResultStream.fromObservable({
        onFailure: (error) => Result.fail(_getCheckoutForm(error)),
      }),
    );
  }
  override checkoutDefault(
    payload: SelfCheckout.PreparedSelfCheckoutFormPayload,
  ): ResultStream.ResultStream<
    SelfCheckout.SelfCheckoutResponseDefault,
    SelfCheckout.SelfCheckoutError
  > {
    const status = SelfCheckout.statusAfterCheckoutUpload(payload);
    return this.unverifiedDonationApi.verifyDonation(payload.payload).pipe(
      map((response) =>
        this._checkoutDefaultResponseAdapter.adapt({
          response: response,
          status: status,
        }),
      ),
      ResultStream.fromObservable({
        onFailure: (error) => Result.fail(getSelfcheckoutError(error)),
      }),
    );
  }
  override checkoutTamaro(
    payload: SelfCheckout.PreparedSelfCheckoutFormPayload,
  ): ResultStream.ResultStream<
    SelfCheckout.SelfCheckoutResponseTamaro,
    SelfCheckout.SelfCheckoutError
  > {
    const status = SelfCheckout.statusAfterCheckoutUpload(payload);
    return this.unverifiedDonationApi.verifyDonation(payload.payload).pipe(
      map((response) =>
        this._checkoutTamaroResponseAdapter.adapt({
          response: response,
          status: status,
        }),
      ),
      ResultStream.fromObservable({
        onFailure: (error) => Result.fail(getSelfcheckoutError(error)),
      }),
    );
  }
  override getUnsubscribeForm(
    hash: SelfCheckout.Hash,
  ): ResultStream.ResultStream<
    SelfCheckout.UnsubscribeForm,
    SelfCheckout.SelfCheckoutError
  > {
    return this.unsubscribeFormApi.getForm(hash).pipe(
      map((result) => this._unsubscribeFormAdapter.adapt(result)),
      ResultStream.fromObservable({
        onFailure: (error) => Result.fail(getSelfcheckoutError(error)),
      }),
    );
  }
  override unsubscribe(
    preparedData: SelfCheckout.PreparedUnsubscribeFormPayload,
  ): ResultStream.ResultStream<void, SelfCheckout.SelfCheckoutError> {
    return this.unsubscribeFormApi
      .unsubscribe(preparedData.hash, preparedData.formData)
      .pipe(
        map(constVoid),
        ResultStream.fromObservable({
          onFailure: (error) => Result.fail(getSelfcheckoutError(error)),
        }),
      );
  }
}

const _getAuthError = (error: unknown): SelfCheckout.SelfCheckoutError => {
  if (error instanceof GeneralHttpError) {
    const defaultError = new SelfCheckout.SelfCheckoutCustomError({
      status: error.status,
      message: error.message,
    });
    switch (error.status) {
      case 410:
        return new SelfCheckout.SelfCheckoutCompletedError();
      case 403: {
        if (
          isString(error.httpError.error.message) &&
          error.httpError.error.message.includes('CSRF')
        ) {
          return new SelfCheckout.SelfCheckoutCSRFError({
            httpError: error.httpError,
          });
        } else {
          return defaultError;
        }
      }
      case 500:
        return defaultError;
      default:
        return defaultError;
    }
  }
  return new SelfCheckout.SelfCheckoutCustomError({});
};

const _getCheckoutForm = (error: unknown): SelfCheckout.SelfCheckoutError => {
  if (error instanceof GeneralHttpError) {
    const defaultError = new SelfCheckout.SelfCheckoutCustomError({
      status: error.status,
      message: error.message,
    });
    switch (error.status) {
      case 410:
        return new SelfCheckout.SelfCheckoutCompletedError();
      case 403: {
        if (
          isString(error.httpError.error.message) &&
          error.httpError.error.message.includes('CSRF')
        ) {
          return new SelfCheckout.SelfCheckoutCSRFError({
            httpError: error.httpError,
          });
        } else {
          return defaultError;
        }
      }
      case 500:
        return defaultError;
      default:
        return defaultError;
    }
  }
  return new SelfCheckout.SelfCheckoutCustomError({});
};

const getSelfcheckoutError = (
  error: unknown,
): SelfCheckout.SelfCheckoutError => {
  if (error instanceof GeneralHttpError) {
    const defaultError = new SelfCheckout.SelfCheckoutCustomError({
      status: error.status,
      message: error.message,
    });
    switch (error.status) {
      case 410:
        if (error.apiErrorMsg?.cause === 'signed') {
          return new SelfCheckout.SelfCheckoutCustomError({
            status: error.status,
          });
        }
        return new SelfCheckout.SelfCheckoutCompletedError();
      case 403: {
        if (
          isString(error.httpError.error.message) &&
          error.httpError.error.message.includes('CSRF')
        ) {
          return new SelfCheckout.SelfCheckoutCSRFError({
            httpError: error.httpError,
          });
        } else {
          return defaultError;
        }
      }
      case 500: {
        if (isString(error.apiErrorMsg)) {
          return new SelfCheckout.SelfCheckoutCustomError({
            status: error.status,
            message: error.apiErrorMsg,
          });
        }
        return defaultError;
      }
      default:
        return defaultError;
    }
  }
  return new SelfCheckout.SelfCheckoutCustomError({});
};
